import { Tooltip } from "antd";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "@/lib/cn";

/** 溢出截断 Tooltip 入参 */
export interface EllipsisTooltipProps {
  /** 完整文案；不传则取 children 字符串 */
  title?: string;
  children: ReactNode;
  className?: string;
}

/**
 * 文本溢出截断时展示 Tooltip，未溢出则不显示气泡
 */
export const EllipsisTooltip = ({ title, children, className }: EllipsisTooltipProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  /** 是否发生溢出截断 */
  const [overflow, setOverflow] = useState(false);
  /** Tooltip 展示文案 */
  const tooltipTitle = title ?? (typeof children === "string" ? children : undefined);

  /** 检测容器是否溢出 */
  const checkOverflow = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setOverflow(el.scrollWidth > el.clientWidth);
  }, []);

  useEffect(() => {
    checkOverflow();
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkOverflow, children, tooltipTitle]);

  const node = (
    <span ref={ref} className={cn("truncate inline-block max-w-full align-bottom", className)}>
      {children}
    </span>
  );

  if (!tooltipTitle || tooltipTitle === "—") return node;
  return overflow ? <Tooltip title={tooltipTitle}>{node}</Tooltip> : node;
};
